import type { Category, CategoryTreeNode, CategoryWriteInput } from '@recipes/contracts';
import { slugify } from '@recipes/domain';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FormField } from '@/features/shared/form-field';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

const ROOT = '__root__';

function flatten(
  nodes: CategoryTreeNode[],
  depth = 0,
): Array<{ node: CategoryTreeNode; depth: number }> {
  return nodes.flatMap((n) => [{ node: n, depth }, ...flatten(n.children, depth + 1)]);
}

export function AdminCategoriesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const [editing, setEditing] = useState<{
    category: Category | null;
    parentId: string | null;
  } | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);
  const tree = useQuery({
    queryKey: queryKeys.taxonomy.categories,
    queryFn: () => api.taxonomy.categories(),
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.taxonomy.categories });
  const remove = useMutation({
    mutationFn: (id: string) => api.admin.deleteCategory(id),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (e) => toastError(e),
  });

  const rows = flatten(tree.data ?? []);

  return (
    <AdminPage
      section="categories"
      description={t('admin.categories.description')}
      actions={
        <Button size="sm" onClick={() => setEditing({ category: null, parentId: null })}>
          <Plus aria-hidden /> {t('admin.categories.create')}
        </Button>
      }
    >
      {tree.isLoading && <Spinner label={t('app.loading')} />}
      {tree.error && <Alert variant="destructive">{describeError(tree.error)}</Alert>}
      {rows.length === 0 && tree.data && (
        <p className="text-sm text-muted-foreground">{t('common.noResults')}</p>
      )}
      <ul className="divide-y rounded-lg border" aria-label={t('admin.sections.categories')}>
        {rows.map(({ node, depth }) => (
          <li
            key={node.id}
            className="flex min-h-[48px] items-center gap-2 px-3 py-1"
            style={{ paddingLeft: `${0.75 + depth * 1.25}rem` }}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">
                {node.name[locale]}{' '}
                <span className="font-normal text-muted-foreground">
                  / {node.name[locale === 'de' ? 'en' : 'de']}
                </span>
              </span>
              <span className="block font-mono text-xs text-muted-foreground">
                {node.slug}
                {node.recipeCount != null &&
                  ` · ${t('collections.recipeCount', { count: node.recipeCount })}`}
              </span>
            </span>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t('admin.categories.addChild', { name: node.name[locale] })}
              onClick={() => setEditing({ category: null, parentId: node.id })}
            >
              <Plus aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t('admin.categories.edit', { name: node.name[locale] })}
              onClick={() => setEditing({ category: node, parentId: node.parentId })}
            >
              <Pencil aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t('admin.categories.delete', { name: node.name[locale] })}
              onClick={() => setDeleting(node)}
            >
              <Trash2 aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      <CategoryDialog
        state={editing}
        all={rows.map((r) => r.node)}
        onClose={() => setEditing(null)}
        onSaved={invalidate}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t('admin.categories.deleteTitle')}
        description={
          deleting ? t('admin.categories.deleteBody', { name: deleting.name[locale] }) : undefined
        }
        destructive
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
    </AdminPage>
  );
}

function CategoryDialog({
  state,
  all,
  onClose,
  onSaved,
}: {
  state: { category: Category | null; parentId: string | null } | null;
  all: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [de, setDe] = useState('');
  const [en, setEn] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [parentId, setParentId] = useState<string>(ROOT);
  useEffect(() => {
    if (!state) return;
    setDe(state.category?.name.de ?? '');
    setEn(state.category?.name.en ?? '');
    setSlug(state.category?.slug ?? '');
    setSlugTouched(Boolean(state.category));
    setParentId(state.parentId ?? ROOT);
  }, [state]);
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(en || de));
  }, [en, de, slugTouched]);

  const save = useMutation({
    mutationFn: () => {
      const body: CategoryWriteInput = {
        parentId: parentId === ROOT ? null : parentId,
        slug: slug.trim(),
        name: { de: de.trim(), en: en.trim() },
      };
      return state?.category
        ? api.admin.updateCategory(state.category.id, body)
        : api.admin.createCategory(body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => toastError(e),
  });
  const valid = de.trim() && en.trim() && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  return (
    <Dialog open={Boolean(state)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>
            {state?.category ? t('admin.categories.editTitle') : t('admin.categories.create')}
          </DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <FormField label={`${t('admin.categories.name')} (de)`} required>
            <Input value={de} maxLength={80} onChange={(e) => setDe(e.target.value)} />
          </FormField>
          <FormField label={`${t('admin.categories.name')} (en)`} required>
            <Input value={en} maxLength={80} onChange={(e) => setEn(e.target.value)} />
          </FormField>
          <FormField
            label={t('admin.categories.slug')}
            required
            hint={t('admin.categories.slugHint')}
          >
            <Input
              value={slug}
              maxLength={80}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
            />
          </FormField>
          <div>
            <Label htmlFor="category-parent">{t('admin.categories.parent')}</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger id="category-parent" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ROOT}>{t('admin.categories.root')}</SelectItem>
                {all
                  .filter((c) => c.id !== state?.category?.id)
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name[locale]}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!valid}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
